import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import config from "./config";
import { apiCall } from "./api";
import Toastr from "./Toastr";

const AdminJoinRequests = ({ onPendingCountChange, firebaseUid }) => {
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRoleByRequest, setSelectedRoleByRequest] = useState({});
  const [joinRequestToast, setJoinRequestToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(config.getApiUrl("selectRoles.php"));
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  }, []);

  const updatePendingCount = useCallback(
    (requests) => {
      if (typeof onPendingCountChange !== "function") return;
      const pendingCount = requests.filter(
        (request) => request?.status === "pending",
      ).length;
      onPendingCountChange(pendingCount);
    },
    [onPendingCountChange],
  );

  const fetchJoinRequests = useCallback(async () => {
    setJoinRequestsLoading(true);
    try {
      // Auth refactor 2026-05-27: server reads firebase_uid from the verified
      // ID token (apiCall attaches it), not from the body. The `firebaseUid`
      // prop is kept for the useCallback dep array so refetches still fire
      // when the caller swaps the active user, but it's no longer transmitted.
      const response = await apiCall("joinRequests.php", {
        method: "POST",
        body: { action: "list" },
      });
      const requests = response?.requests || [];
      setJoinRequests(requests);
      updatePendingCount(requests);
    } catch (error) {
      console.error("Error fetching join requests:", error);
      setJoinRequests([]);
      updatePendingCount([]);
    } finally {
      setJoinRequestsLoading(false);
    }
  }, [updatePendingCount]);

  useEffect(() => {
    fetchRoles();
    fetchJoinRequests();
  }, [fetchRoles, fetchJoinRequests]);

  useEffect(() => {
    if (roles.length === 0 || joinRequests.length === 0) return;
    const defaultRole =
      roles.find((role) => String(role.role_name).toLowerCase() === "member") ||
      roles[0];
    if (!defaultRole) return;
    setSelectedRoleByRequest((prev) => {
      const next = { ...prev };
      joinRequests.forEach((request) => {
        if (request?.status !== "pending") return;
        if (!next[request.id]) {
          next[request.id] = defaultRole.role_id;
        }
      });
      return next;
    });
  }, [joinRequests, roles]);

  const handleJoinRequestDecision = async (requestId, nextStatus) => {
    const selectedRole = selectedRoleByRequest[requestId];
    if (nextStatus === "approved" && !selectedRole) {
      setJoinRequestToast({
        open: true,
        message: "Select a role before approving.",
        severity: "warning",
      });
      return;
    }
    try {
      // Auth refactor 2026-05-27: 'decide' is now an admin-gated endpoint —
      // the server resolves the caller from the verified ID token (apiCall
      // attaches it) and checks they're an admin on the target project.
      // Anonymous or non-admin callers get 403.
      const response = await apiCall("joinRequests.php", {
        method: "POST",
        body: {
          action: "decide",
          request_id: requestId,
          status: nextStatus,
          role_id: nextStatus === "approved" ? selectedRole : null,
        },
      });

      if (!response?.success) {
        throw new Error(response?.error || "Request failed");
      }

      setJoinRequestToast({
        open: true,
        message: `Request ${nextStatus}.`,
        severity: "success",
      });
      fetchJoinRequests();
    } catch (error) {
      console.error("Error updating join request:", error);
      setJoinRequestToast({
        open: true,
        message: "Failed to update join request.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ width: "100%", padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Join Requests
      </Typography>
      {joinRequestsLoading && (
        <Typography variant="body2">Loading join requests...</Typography>
      )}
      {!joinRequestsLoading && joinRequests.length === 0 && (
        <Typography variant="body2">No join requests yet.</Typography>
      )}
      {!joinRequestsLoading && joinRequests.length > 0 && (
        <List sx={{ width: "100%" }}>
          {joinRequests.map((request) => (
            <ListItem
              key={request.id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                mb: 2,
                flexDirection: "column",
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="h6">
                    {request.display_name || request.email || "Unknown user"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.email || "No email provided"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color:
                      request.status === "pending"
                        ? "var(--theme-danger-clr)"
                        : "var(--theme-color)",
                  }}
                >
                  {request.status === "pending" ? "Pending" : request.status}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Project:{" "}
                {request.project_name ||
                  request.projectid ||
                  request.projectsid}
              </Typography>
              {request.created_at && (
                <Typography variant="caption" color="text.secondary">
                  Requested: {new Date(request.created_at).toLocaleString()}
                </Typography>
              )}
              {request.status === "pending" && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Select
                    size="small"
                    value={selectedRoleByRequest[request.id] || ""}
                    onChange={(event) =>
                      setSelectedRoleByRequest((prev) => ({
                        ...prev,
                        [request.id]: event.target.value,
                      }))
                    }
                    sx={{ minWidth: 180 }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select role
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.role_id} value={role.role_id}>
                        {role.role_name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() =>
                      handleJoinRequestDecision(request.id, "approved")
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() =>
                      handleJoinRequestDecision(request.id, "denied")
                    }
                  >
                    Deny
                  </Button>
                </Box>
              )}
            </ListItem>
          ))}
        </List>
      )}
      <Toastr
        open={joinRequestToast.open}
        message={joinRequestToast.message}
        severity={joinRequestToast.severity}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setJoinRequestToast((prev) => ({ ...prev, open: false }));
        }}
      />
    </Box>
  );
};

export default AdminJoinRequests;
